/**
 * Centralized Error Tracking and Logging
 * Best Practice: Single source of truth for error handling
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  metadata?: Record<string, any>;
}

export class AppError extends Error {
  public readonly context: ErrorContext;
  public readonly timestamp: Date;
  public readonly severity: 'low' | 'medium' | 'high' | 'critical';

  constructor(
    message: string, 
    context: ErrorContext = {}, 
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'
  ) {
    super(message);
    this.name = 'AppError';
    this.context = context;
    this.timestamp = new Date();
    this.severity = severity;
  }
}

class ErrorTracker {
  private errors: AppError[] = [];
  private readonly maxErrors = 100; // Keep last 100 errors
  private readonly isDevelopment = import.meta.env.DEV;

  /**
   * Track an error with context
   */
  track(error: Error | AppError, context: ErrorContext = {}) {
    const appError = error instanceof AppError 
      ? error 
      : new AppError(error.message, context);

    // Add to error queue
    this.errors.push(appError);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift(); // Remove oldest error
    }

    // Log to console in development
    if (this.isDevelopment) {
      console.group(`🔴 Error: ${appError.message}`);
      console.log('Severity:', appError.severity);
      console.log('Component:', appError.context.component);
      console.log('Action:', appError.context.action);
      console.log('Timestamp:', appError.timestamp.toISOString());
      if (appError.context.metadata) {
        console.log('Metadata:', appError.context.metadata);
      }
      console.trace();
      console.groupEnd();
    }

    // In production, you could send to error tracking service
    // Example: Sentry, LogRocket, etc.
    if (!this.isDevelopment && appError.severity === 'critical') {
      this.reportToService(appError);
    }

    return appError;
  }

  /**
   * Track async operation errors
   */
  async trackAsync<T>(
    operation: () => Promise<T>,
    context: ErrorContext
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.track(error as Error, context);
      throw error;
    }
  }

  /**
   * Get recent errors
   */
  getRecentErrors(count: number = 10): AppError[] {
    return this.errors.slice(-count);
  }

  /**
   * Get errors by severity
   */
  getErrorsBySeverity(severity: 'low' | 'medium' | 'high' | 'critical'): AppError[] {
    return this.errors.filter(e => e.severity === severity);
  }

  /**
   * Clear error queue
   */
  clear() {
    this.errors = [];
  }

  /**
   * Report to external service
   * In production, this would send to Sentry, LogRocket, or similar service
   */
  private reportToService(error: AppError) {
    // For now, we store critical errors for manual review
    // In production, integrate with error tracking service:
    // - Sentry: Sentry.captureException(error, { contexts: { custom: error.context } })
    // - LogRocket: LogRocket.captureException(error, { tags: error.context })
    
    if (import.meta.env.DEV) {
      console.error('Critical error:', {
        message: error.message,
        context: error.context,
        timestamp: error.timestamp,
        stack: error.stack
      });
    }
    
    // Store in localStorage for admin review (production fallback)
    try {
      const stored = localStorage.getItem('critical_errors') || '[]';
      const errors = JSON.parse(stored);
      errors.push({
        message: error.message,
        context: error.context,
        timestamp: error.timestamp.toISOString(),
        stack: error.stack
      });
      // Keep only last 50 critical errors
      localStorage.setItem('critical_errors', JSON.stringify(errors.slice(-50)));
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Get error statistics
   */
  getStatistics() {
    const total = this.errors.length;
    const bySeverity = {
      low: this.errors.filter(e => e.severity === 'low').length,
      medium: this.errors.filter(e => e.severity === 'medium').length,
      high: this.errors.filter(e => e.severity === 'high').length,
      critical: this.errors.filter(e => e.severity === 'critical').length,
    };
    const byComponent = this.errors.reduce((acc, error) => {
      const component = error.context.component || 'unknown';
      acc[component] = (acc[component] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      bySeverity,
      byComponent,
      recent: this.getRecentErrors(5)
    };
  }
}

// Singleton instance
export const errorTracker = new ErrorTracker();

/**
 * Utility function for error boundaries
 */
export function logError(error: Error, errorInfo?: { componentStack: string }) {
  errorTracker.track(error, {
    component: 'ErrorBoundary',
    metadata: errorInfo
  });
}
